import { GetServerSideProps } from 'next';

import { PropertyCard } from '@/components/property';

import { AppObject, Property } from '@/types';
import { sentryCaptureException, transformProperty } from '@/utility';
import { useRef, useState } from 'react';
import { updateFavoriteById } from '@/context/favorite/hooks/use-favorite';
import { Navbar2 } from '@/components/common/navbar';
import { getTokenInfo, validateToken } from '@/utility-backend/auth';
import { FavoriteProperty } from '@/context/favorite/types';
import { getFavoritePropeties } from '../api/favorites';
import { ReplierListingService } from '@/services/repliers';
import useBoundingClientRect from '@/hooks/use-bounding-client-rect';
import { useScrollPosition } from '@/hooks/use-scroll-position';
import clsx from 'clsx';

export interface PropertySearchPageProps {
  properties: Property[];
  favoritesProperties: FavoriteProperty[];
  errorMessage?: string;
}

export default function PropertySearchPage({
  properties: propertiesFromServer,
  favoritesProperties,
}: PropertySearchPageProps) {
  const [properties, setProperties] = useState(propertiesFromServer);
  const [isRemoving, setIsRemoving] = useState(false);

  const navbarRef = useRef(null);
  const bounds = useBoundingClientRect(navbarRef, 1000);
  const scroll = useScrollPosition(500);

  const handleRemoveProperty = async (property: Property) => {
    try {
      setIsRemoving(true);
      await updateFavoriteById(property.propertyNumber, false);
      setProperties(
        properties.filter((p) => p.propertyNumber !== property.propertyNumber)
      );
      setIsRemoving(false);
    } catch (error) {
      console.error(error);
      setIsRemoving(false);
      sentryCaptureException(error);
    }
  };

  const navbar = (
    <div>
      <div
        ref={navbarRef}
        className={clsx(
          'fixed w-full bg-white z-50',
          scroll.top > 0 && 'shadow-md'
        )}
      >
        <Navbar2 title="My favourite properties" />
      </div>
      <div style={{ height: bounds?.height }}></div>
    </div>
  );

  if (!properties?.length) {
    return (
      <div className="flex flex-col">
        {navbar}
        <hr />
        <div className="flex items-center flex-1 flex-col py-28">
          <p className="text-2xl font-semibold text-gray-600">
            No favourite property found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {navbar}
      <hr />

      <div className="mt-8 px-4 md:px-8">
        <div className="grid grid-col-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-2">
          {properties.map((el) => (
            <PropertyCard
              property={el}
              key={el.id}
              showFavIcon
              favInfo={{
                isFav: true,
                toggleFav: () => handleRemoveProperty(el),
                loading: isRemoving,
                addedOn: favoritesProperties.find(
                  (x) => x.propertyNumber === el.propertyNumber
                )?.createdOn,
                message: favoritesProperties.find(
                  (x) => x.propertyNumber === el.propertyNumber
                )?.message,
                addedBy: favoritesProperties.find(
                  (x) => x.propertyNumber === el.propertyNumber
                )?.addedBy,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps<
  PropertySearchPageProps
> = async (context) => {
  if (!validateToken(context.req.cookies.jdr_token)) {
    return {
      props: {
        properties: [],
        favoritesProperties: [],
      },
    };
  }

  const authInfo = getTokenInfo(context.req.cookies.jdr_token);
  let favoritesProperty: FavoriteProperty[] = [];

  try {
    favoritesProperty = await getFavoritePropeties(authInfo.email);
  } catch (error) {
    console.error(error);
    return {
      props: {
        properties: [],
        favoritesProperties: [],
        errorMessage: 'Unable to fetch favorite properties',
      },
    };
  }

  const propertyNumbers: string[] = favoritesProperty.map(
    (el) => el.propertyNumber
  );

  if (!propertyNumbers.length) {
    return {
      props: {
        properties: [],
        favoritesProperties: [],
      },
    };
  }

  const query: AppObject = {
    propertyNumber: propertyNumbers,
  };

  query.status = ['A', 'U'];

  const client = new ReplierListingService();
  const response = await client.findListings(query);

  const properties: Property[] = (response?.listings || []).map(
    transformProperty
  );

  properties.sort((a, b) => {
    return (
      propertyNumbers.indexOf(a.propertyNumber) -
      propertyNumbers.indexOf(b.propertyNumber)
    );
  });

  return {
    props: {
      properties: properties,
      favoritesProperties: favoritesProperty,
    },
  };
};
